/**
 * PrimeVue Passthrough configuration for unstyled mode.
 * Uses Bootstrap/Phoenix CSS classes and custom CSS for styling.
 */
export const primeVuePassthrough = {
  datatable: {
    root: ({
      props,
    }: {
      props: { resizableColumns: boolean; columnResizeMode: string };
    }) => ({
      class: [
        "pv-datatable",
        {
          "pv-datatable-resizable": props.resizableColumns,
          "pv-datatable-resizable-fit":
            props.resizableColumns && props.columnResizeMode === "fit",
        },
      ],
    }),
    tableContainer: "pv-datatable-container",
    table: "pv-datatable-table",
    thead: "pv-datatable-thead",
    tbody: "pv-datatable-tbody",
    headerRow: "pv-datatable-header-row",
    headerCell: "pv-datatable-header-cell",
    bodyRow: ({ context }: { context: { selected: boolean } }) => ({
      class: [
        "pv-datatable-body-row",
        { "pv-datatable-row-selected": context.selected },
      ],
    }),
    bodyCell: "pv-datatable-body-cell",
    emptyMessage: "pv-datatable-empty",
    virtualScroller: "pv-datatable-scroller",
    virtualScrollerSpacer: "pv-datatable-vs-spacer",
    wrapper: "pv-datatable-wrapper",
    columnResizeIndicator: "pv-datatable-column-resize-indicator",
    // Column-related passthrough
    column: {
      headerContent: "pv-datatable-header-content",
      headerTitle: "pv-datatable-header-title",
      sort: "pv-datatable-sort",
      sortIcon: "pv-datatable-sort-icon",
      sortBadge: "pv-datatable-sort-badge",
      columnResizer: {
        class: "pv-datatable-column-resizer",
        onMousedown: swallowClickEndingResize,
      },
    },
    // Selection checkbox
    pcHeaderCheckbox: {
      root: "pv-checkbox",
      box: "pv-checkbox-box",
      input: "pv-checkbox-input",
      icon: "pv-checkbox-icon",
    },
    pcRowCheckbox: {
      root: "pv-checkbox",
      box: "pv-checkbox-box",
      input: "pv-checkbox-input",
      icon: "pv-checkbox-icon",
    },
  },
  column: {
    root: "pv-column",
    headerCell: "pv-datatable-header-cell",
    headerContent: "pv-datatable-header-content",
    headerTitle: "pv-datatable-header-title",
    sort: "pv-datatable-sort",
    sortIcon: "pv-datatable-sort-icon",
    bodyCell: "pv-datatable-body-cell",
    filterInput: "pv-column-filter-input",
    filterMenuButton: "pv-column-filter-menu-button",
    filterOverlay: "pv-column-filter-overlay",
    filterConstraint: "pv-column-filter-constraint",
    filterMatchModeDropdown: "pv-column-filter-matchmode",
    filterAddRuleButton: "pv-column-filter-add-rule",
    filterRemoveRuleButton: "pv-column-filter-remove-rule",
    filterButtonbar: "pv-column-filter-buttonbar",
    filterClearButton: "pv-column-filter-clear",
    filterApplyButton: "pv-column-filter-apply",
  },
  inputtext: {
    root: "pv-inputtext form-control form-control-sm",
  },
  multiselect: {
    root: ({
      state,
    }: {
      state: { focused: boolean; overlayVisible: boolean };
    }) => ({
      class: [
        "pv-multiselect",
        {
          "pv-multiselect-focused": state.focused,
          "pv-multiselect-open": state.overlayVisible,
        },
      ],
    }),
    labelContainer: "pv-multiselect-label-container",
    label: ({ props }: { props: { modelValue: unknown[] } }) => ({
      class: [
        "pv-multiselect-label",
        {
          "pv-multiselect-label-empty":
            !props.modelValue || props.modelValue.length === 0,
        },
      ],
    }),
    trigger: "pv-multiselect-trigger",
    triggerIcon: "pv-multiselect-trigger-icon",
    dropdown: "pv-multiselect-dropdown",
    overlay: "pv-multiselect-overlay",
    header: "pv-multiselect-header",
    filterContainer: "pv-multiselect-filter-container",
    pcFilterContainer: {
      root: "pv-multiselect-filter-container",
    },
    filterInput: "pv-multiselect-filter-input form-control form-control-sm",
    pcFilter: {
      root: "pv-multiselect-filter-input form-control form-control-sm",
    },
    filterIcon: "pv-multiselect-filter-icon",
    listContainer: "pv-multiselect-list-wrapper",
    list: "pv-multiselect-list",
    optionGroup: "pv-multiselect-option-group",
    option: ({
      context,
    }: {
      context: { selected: boolean; focused: boolean; disabled: boolean };
    }) => ({
      class: [
        "pv-multiselect-option",
        {
          "pv-multiselect-option-selected": context.selected,
          "pv-multiselect-option-focused": context.focused,
          "pv-multiselect-option-disabled": context.disabled,
        },
      ],
    }),
    optionCheckbox: {
      root: "pv-checkbox pv-checkbox-sm",
      box: "pv-checkbox-box",
      input: "pv-checkbox-input",
      icon: "pv-checkbox-icon",
    },
    emptyMessage: "pv-multiselect-empty",
    chip: "pv-multiselect-chip",
    chipLabel: "pv-multiselect-chip-label",
    removeTokenIcon: "pv-multiselect-remove-icon",
    clearIcon: "pv-multiselect-clear-icon",
    // Header checkbox (select all)
    pcHeaderCheckbox: {
      root: "pv-checkbox",
      box: "pv-checkbox-box",
      input: "pv-checkbox-input",
      icon: "pv-checkbox-icon",
    },
  },
  datepicker: {
    root: "pv-datepicker",
    pcInputText: {
      root: "pv-inputtext form-control form-control-sm",
    },
    panel: "pv-datepicker-panel",
    calendarContainer: "pv-datepicker-calendar-container",
    calendar: "pv-datepicker-calendar",
    header: "pv-datepicker-header",
    title: "pv-datepicker-title",
    selectMonth: "pv-datepicker-select-month",
    selectYear: "pv-datepicker-select-year",
    pcPrevButton: {
      root: "pv-datepicker-nav-btn",
    },
    pcNextButton: {
      root: "pv-datepicker-nav-btn",
    },
    dayView: "pv-datepicker-day-view",
    tableHeader: "pv-datepicker-table-header",
    tableHeaderRow: "pv-datepicker-table-header-row",
    tableHeaderCell: "pv-datepicker-table-header-cell",
    weekDay: "pv-datepicker-weekday",
    tableBody: "pv-datepicker-table-body",
    tableBodyRow: "pv-datepicker-table-body-row",
    dayCell: "pv-datepicker-day-cell",
    day: ({
      context,
    }: {
      context: { selected: boolean; disabled: boolean; today: boolean };
    }) => ({
      class: [
        "pv-datepicker-day",
        {
          "pv-datepicker-day-selected": context.selected,
          "pv-datepicker-day-today": context.today,
          "pv-datepicker-day-disabled": context.disabled,
        },
      ],
    }),
    monthView: "pv-datepicker-cell-view",
    month: ({
      context,
    }: {
      context: { selected: boolean; disabled: boolean };
    }) => ({
      class: [
        "pv-datepicker-cell",
        {
          "pv-datepicker-day-selected": context.selected,
          "pv-datepicker-day-disabled": context.disabled,
        },
      ],
    }),
    yearView: "pv-datepicker-cell-view",
    year: ({
      context,
    }: {
      context: { selected: boolean; disabled: boolean };
    }) => ({
      class: [
        "pv-datepicker-cell",
        {
          "pv-datepicker-day-selected": context.selected,
          "pv-datepicker-day-disabled": context.disabled,
        },
      ],
    }),
    buttonbar: "pv-datepicker-buttonbar",
    pcTodayButton: {
      root: "pv-datepicker-buttonbar-btn",
    },
    pcClearButton: {
      root: "pv-datepicker-buttonbar-btn",
    },
  },
  checkbox: {
    root: "pv-checkbox",
    box: "pv-checkbox-box",
    input: "pv-checkbox-input",
    icon: "pv-checkbox-icon",
  },
  virtualscroller: {
    root: "pv-virtualscroller",
    content: "pv-virtualscroller-content",
    spacer: "pv-virtualscroller-spacer",
    loader: "pv-virtualscroller-loader",
    loadingIcon: "pv-virtualscroller-loading-icon",
  },
};

export default primeVuePassthrough;

/**
 * A resize drag that ends inside its own header cell makes the browser fire a click on that cell,
 * which PrimeVue treats as a sort request. Swallow the one click the drag produces; the timeout
 * disarms the swallower when the drag ends somewhere that yields no click, so it never eats the
 * user's next genuine click.
 */
function swallowClickEndingResize() {
  const swallow = (event: MouseEvent) => event.stopPropagation();
  window.addEventListener("click", swallow, { capture: true, once: true });
  window.addEventListener(
    "mouseup",
    () =>
      setTimeout(() =>
        window.removeEventListener("click", swallow, { capture: true })
      ),
    { once: true }
  );
}
